import { useState } from "react";
import { Siren, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : null);

export function UrgentChip({ urgent, testId }) {
  if (!urgent?.flagged) return null;
  return (
    <span data-testid={testId} title={urgent.reason} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${urgent.acknowledged_at ? "border-jigsaw-border bg-white text-jigsaw-muted" : "border-jigsaw-danger/30 bg-jigsaw-dangerBg text-jigsaw-danger"}`}>
      <Siren className="h-3 w-3" /> Client urgent{urgent.acknowledged_at ? " · seen" : ""}
    </span>
  );
}

/* Client-raised urgency marker (D2). Informational only: no priority, ordering or response-time change is implied. */
export function UrgentPanel({ urgent, canAcknowledge, onAcknowledge, testId = "urgent-panel" }) {
  if (!urgent?.flagged) return null;
  return (
    <div data-testid={testId} className={`rounded-xl border px-5 py-4 ${urgent.acknowledged_at ? "border-jigsaw-border bg-white" : "border-jigsaw-danger/30 bg-jigsaw-dangerBg"}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-jigsaw-navy"><Siren className="h-4 w-4 text-jigsaw-danger" /> Marked urgent by the client{urgent.flagged_at ? <span className="font-normal text-jigsaw-muted">· {when(urgent.flagged_at)}</span> : null}</p>
          <p data-testid={`${testId}-reason`} className="mt-1 whitespace-pre-wrap text-sm text-jigsaw-navy">{urgent.reason}</p>
          <p data-testid={`${testId}-ack`} className="mt-2 flex items-center gap-1.5 text-[12px] text-jigsaw-muted">
            {urgent.acknowledged_at ? <><CheckCircle2 className="h-3.5 w-3.5 text-jigsaw-success" /> Seen by {urgent.acknowledged_by_name || "Jigsaw"} on {when(urgent.acknowledged_at)}</> : "Not yet acknowledged by Jigsaw"}
          </p>
          <p className="mt-1 text-[11px] text-jigsaw-muted">This marker does not change priority, queue order or any response-time commitment.</p>
        </div>
        {canAcknowledge && !urgent.acknowledged_at && <Button data-testid={`${testId}-acknowledge`} size="sm" variant="outline" onClick={onAcknowledge}>Acknowledge</Button>}
      </div>
    </div>
  );
}

export function MarkUrgentDialog({ enquiryId, onFlagged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 5) return toast.error("Tell Jigsaw why this is urgent (at least 5 characters).");
    setBusy(true);
    try {
      await api.post(`/client/requests/${enquiryId}/urgent`, { reason: reason.trim() });
      toast.success("Jigsaw has been shown this request as urgent");
      setOpen(false); setReason(""); onFlagged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark this request as urgent"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="mark-urgent-button" size="sm" variant="outline"><Siren className="mr-1.5 h-3.5 w-3.5" /> Mark as urgent</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Mark this request as urgent</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="urgent-reason">Why is it urgent?</Label>
          <Textarea id="urgent-reason" data-testid="mark-urgent-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. family arriving tomorrow with no accommodation" />
          <p className="text-[12px] text-jigsaw-muted">Your coordinator sees this marker and reason. Jigsaw decides priority; marking urgent does not promise a response time.</p>
        </div>
        <DialogFooter><Button data-testid="mark-urgent-submit" onClick={submit} disabled={busy}>Send to Jigsaw</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
